/**
 * Client side of the ephemeral-branch routes of `waadoo-projects`
 * (backend `plugins/waadooProjects/branchRoutes.ts`): the creation form's
 * figures, creation, deletion, whether a branch exists in git yet, and which
 * ephemeral branches are in git (for the « being deleted » state,
 * `branchDeletion.ts`).
 *
 * The types are re-declared rather than imported from the backend, as in
 * `pipelines.ts`: they are the contract of the routes and change on both
 * sides at once.
 *
 * UX ONLY, every check below. The backend applies the same naming rule, the
 * cap and the editor rule again on every call; the form only says early what
 * it would refuse. The name's length bound is the backend's
 * (`nameMaxLength`, computed per project from the environment's resource
 * names), never recomputed here.
 *
 * ONE META READ PER PROJECT for every screen (2026-10-03): the project page,
 * the branch page and the sidebar all ask; one answer is shared for
 * {@link BRANCH_META_TTL_MS} (`sharedLoad.ts`), per `FetchApi` instance as in
 * `grafanaOrgs.ts`. `reload()` bypasses it; a creation or a deletion made
 * here forgets it (`forgetBranchReads`, `branchReads.ts`).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import { EPHEMERAL_BRANCH_PREFIX } from './branchKind';
import { onBranchReadsForgotten } from './branchReadsSignal';
import { clearPendingDeletion, markPendingDeletion } from './pendingDeletions';
import {
  createSharedLoad,
  type SharedLoad,
  type SharedLoadOptions,
} from './sharedLoad';

/** How many ephemeral branches a project has in git, against its cap. */
export interface EphemeralQuota {
  used: number;
  max: number;
  /** No branch may be created until one is deleted. */
  reached: boolean;
}

/**
 * The quota of {@link BranchMeta}: the git count (`ephemeralCount`) against
 * the backend's cap (`max`, its `MAX_EPHEMERAL_BRANCHES`) — the figures of
 * the sidebar's « Éphémères · 2/10 », the creation dialog and the
 * environments card. The count is null only for those who may not manage
 * branches, to whom none of them is offered; read as 0 then, as the dialog
 * always did.
 */
export function ephemeralQuota(meta: BranchMeta): EphemeralQuota {
  const used = meta.ephemeralCount ?? 0;
  return { used, max: meta.max, reached: used >= meta.max };
}

/** The backend plugin serving the routes. */
const PLUGIN_ID = 'waadoo-projects';

/** Mirrored from the backend's `BranchMetaResponse`. */
export interface BranchMeta {
  canManage: boolean;
  ephemeralCount: number | null;
  max: number;
  nameMaxLength: number;
  prefix: string;
  sources: string[];
}

/** Mirrored from the backend's `BranchCreatedResponse`. */
export interface BranchCreated {
  branch: string;
  path: string;
}

/** Mirrored from the backend's `BranchStatusResponse`. */
export interface BranchStatus {
  inGit: boolean;
  repoUrl: string;
}

/** Mirrored from the backend's `EphemeralBranchesResponse`. */
export interface EphemeralBranches {
  branches: string[];
  /** When the backend started reading GitLab (ISO). */
  readAt: string;
  /** False when the list may have been cut by GitLab's page. */
  complete: boolean;
}

/** Mirrored from the backend's `BranchErrorCode`. */
export type BranchErrorCode =
  | 'invalid-project'
  | 'invalid-name'
  | 'invalid-source'
  | 'invalid-branch'
  | 'not-found'
  | 'unknown-source'
  | 'branch-exists'
  | 'limit-reached'
  | 'branch-protected'
  | 'gitlab-unavailable';

/** A refusal of the backend, by its code; `unknown` for anything else. */
export class BranchRequestError extends Error {
  readonly code: BranchErrorCode | 'unknown';

  constructor(code: BranchErrorCode | 'unknown', status: number) {
    super(`Branch API: HTTP ${status} (${code})`);
    this.name = 'BranchRequestError';
    this.code = code;
  }
}

/**
 * The shape of a new name, mirrored from the backend's
 * `EPHEMERAL_BRANCH_PATTERN`: lowercase letters and digits in
 * hyphen-separated runs after the prefix.
 */
const SUFFIX_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Why the part typed after the fixed prefix is not a valid name yet.
 * `empty` is not shown as an error, only keeps the button disabled.
 */
export type SuffixProblem = 'empty' | 'chars' | 'hyphen' | 'too-long';

export function validateBranchSuffix(
  suffix: string,
  nameMaxLength: number,
): SuffixProblem | undefined {
  if (suffix === '') {
    return 'empty';
  }
  if (/[^a-z0-9-]/.test(suffix)) {
    return 'chars';
  }
  if (!SUFFIX_PATTERN.test(suffix)) {
    return 'hyphen';
  }
  if (EPHEMERAL_BRANCH_PREFIX.length + suffix.length > nameMaxLength) {
    return 'too-long';
  }
  return undefined;
}

/** How many characters the suffix may still take; negative when over. */
export function remainingLength(suffix: string, nameMaxLength: number): number {
  return nameMaxLength - EPHEMERAL_BRANCH_PREFIX.length - suffix.length;
}

/**
 * The address the new branch's application will have, when the project's
 * existing environments show the pattern: the ApplicationSet gives every
 * ephemeral environment `https://<hostPrefix><repo>-<branch>.<baseDomain>`
 * — `hostPrefix` the type's `chart.hostPrefix` (`odoo-` for Odoo) — and the
 * app knows neither `<repo>` nor `<baseDomain>` otherwise. So the domain is
 * read off an environment whose address follows that very shape for its own
 * branch — `<hostPrefix><project>-<its branch>.` — and nothing is guessed:
 * no such environment, or no descriptor to say the prefix (undefined), no
 * preview.
 */
export function previewAppUrl(
  environments: ReadonlyArray<{ branch: string; url?: string }>,
  project: string,
  branch: string,
  hostPrefix: string | undefined,
): string | undefined {
  if (hostPrefix === undefined) {
    return undefined;
  }
  for (const env of environments) {
    if (!env.url) {
      continue;
    }
    let host: string;
    try {
      const url = new URL(env.url);
      if (url.protocol !== 'https:') {
        continue;
      }
      host = url.hostname;
    } catch {
      continue;
    }
    const label = `${hostPrefix}${project}-${env.branch}.`;
    if (host.startsWith(label) && host.length > label.length) {
      return `https://${hostPrefix}${project}-${branch}.${host.slice(
        label.length,
      )}`;
    }
  }
  return undefined;
}

async function baseUrl(discoveryApi: DiscoveryApi, project: string) {
  // Absolute backend URL: in development the frontend runs on another port.
  const base = await discoveryApi.getBaseUrl(PLUGIN_ID);
  return `${base}/projects/${encodeURIComponent(project)}/branches`;
}

async function readError(response: Response): Promise<BranchRequestError> {
  let code: BranchErrorCode | 'unknown' = 'unknown';
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string') {
      code = body.error as BranchErrorCode;
    }
  } catch {
    // A body that is not JSON (a proxy's error page) is an unknown failure.
  }
  return new BranchRequestError(code, response.status);
}

interface Apis {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}

export async function fetchBranchMeta(
  apis: Apis & { project: string },
): Promise<BranchMeta> {
  const response = await apis.fetchApi.fetch(
    `${await baseUrl(apis.discoveryApi, apis.project)}/meta`,
  );
  if (!response.ok) {
    throw await readError(response);
  }
  return (await response.json()) as BranchMeta;
}

export async function createBranch(
  apis: Apis & { project: string; name: string; from: string },
): Promise<BranchCreated> {
  const response = await apis.fetchApi.fetch(
    await baseUrl(apis.discoveryApi, apis.project),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: apis.name, from: apis.from }),
    },
  );
  if (!response.ok) {
    throw await readError(response);
  }
  // A branch of the same name deleted a moment ago from this tab must not
  // show the new one as « being deleted ».
  clearPendingDeletion(apis.project, apis.name);
  return (await response.json()) as BranchCreated;
}

export async function deleteBranch(
  apis: Apis & { project: string; branch: string },
): Promise<void> {
  const response = await apis.fetchApi.fetch(
    `${await baseUrl(apis.discoveryApi, apis.project)}/${encodeURIComponent(
      apis.branch,
    )}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw await readError(response);
  }
  // Shown as « being deleted » at once, before ArgoCD or GitLab is read
  // again (`pendingDeletions.ts`).
  markPendingDeletion(apis.project, apis.branch);
}

export async function fetchBranchStatus(
  apis: Apis & { project: string; branch: string },
): Promise<BranchStatus> {
  const response = await apis.fetchApi.fetch(
    `${await baseUrl(apis.discoveryApi, apis.project)}/${encodeURIComponent(
      apis.branch,
    )}/status`,
  );
  if (!response.ok) {
    throw await readError(response);
  }
  return (await response.json()) as BranchStatus;
}

/**
 * The ephemeral branches of `project` in git. Rejects on any failure — the
 * route not registered (404 elsewhere than ludo), a refusal, GitLab down —
 * which every caller reads as « unknown ».
 */
export async function fetchEphemeralBranches(
  apis: Apis & { project: string },
): Promise<EphemeralBranches> {
  const response = await apis.fetchApi.fetch(
    `${await baseUrl(apis.discoveryApi, apis.project)}/ephemeral`,
  );
  if (!response.ok) {
    throw await readError(response);
  }
  return (await response.json()) as EphemeralBranches;
}

/**
 * How long one meta answer is shared: the backend's own cache of the GitLab
 * reads behind it (`BRANCH_READ_TTL_MS`).
 */
export const BRANCH_META_TTL_MS = 30_000;

/** Never pruned: one entry per project opened in the session, gone on reload. */
const metaLoads = new WeakMap<FetchApi, Map<string, SharedLoad<BranchMeta>>>();

/**
 * The shared read behind {@link useBranchMeta}. Rejects when the request
 * fails; the failure is not kept.
 */
export function loadBranchMeta(
  apis: Apis & { project: string },
  options?: SharedLoadOptions,
): Promise<BranchMeta> {
  return sharedMeta(apis.fetchApi, apis.project).load(
    () => fetchBranchMeta(apis),
    options,
  );
}

/**
 * The shared read of `project`'s figures, made on first use and kept — its
 * subscribers with it (`useBranchMeta`).
 */
function sharedMeta(
  fetchApi: FetchApi,
  project: string,
): SharedLoad<BranchMeta> {
  let byProject = metaLoads.get(fetchApi);
  if (!byProject) {
    byProject = new Map();
    metaLoads.set(fetchApi, byProject);
  }
  let shared = byProject.get(project);
  if (!shared) {
    shared = createSharedLoad<BranchMeta>(BRANCH_META_TTL_MS);
    byProject.set(project, shared);
  }
  return shared;
}

/**
 * The next {@link loadBranchMeta} of `project` asks the backend again.
 * Forgotten and not dropped: the instances of `useBranchMeta` stay
 * subscribed to it.
 */
export function forgetBranchMeta(fetchApi: FetchApi, project: string): void {
  metaLoads.get(fetchApi)?.get(project)?.forget();
}

/**
 * The creation form's figures for `project`, and a way to read them again
 * — `reload` bypasses the shared answer (Refresh). Read again on their own
 * after a creation or a deletion made from this app (`branchReadsSignal.ts`).
 * Every answer fetched for the project, by any instance, reaches them all
 * (`SharedLoad.subscribe`): the project page's Refresh updates the
 * sidebar's « Éphémères · N/max » too.
 * Undefined while asked and on ANY failure — a 404 where the routes are not
 * registered, a refused check, GitLab down: the entry points then simply do
 * not appear. Only `canManage: true` shows them.
 */
export function useBranchMeta(project: string | undefined): {
  meta: BranchMeta | undefined;
  reload: () => void;
} {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  // Kept with the project it answers for: a page moving to another project
  // must not show the previous one's figures meanwhile.
  const [answer, setAnswer] = useState<{ project: string; meta: BranchMeta }>();
  const [generation, setGeneration] = useState(0);
  // Set by `reload`, consumed by the load it triggers.
  const forceNext = useRef(false);
  const reload = useCallback(() => {
    forceNext.current = true;
    setGeneration(current => current + 1);
  }, []);

  // A branch of the project created or deleted from this app
  // (`forgetBranchReads`): the figures again — not forced, the shared answer
  // is already gone, so every screen showing them shares the one request.
  useEffect(() => {
    if (!project) {
      return undefined;
    }
    return onBranchReadsForgotten(fetchApi, forgotten => {
      if (forgotten === project) {
        setGeneration(current => current + 1);
      }
    });
  }, [project, fetchApi]);

  // Whoever fetched them: another instance's Refresh, its read after a
  // creation or a deletion.
  useEffect(() => {
    if (!project) {
      return undefined;
    }
    return sharedMeta(fetchApi, project).subscribe(meta =>
      setAnswer({ project, meta }),
    );
  }, [project, fetchApi]);

  useEffect(() => {
    if (!project) {
      return undefined;
    }
    let active = true;
    const force = forceNext.current;
    forceNext.current = false;
    const load = async () => {
      try {
        const meta = await loadBranchMeta(
          { fetchApi, discoveryApi, project },
          { force },
        );
        if (active) {
          setAnswer({ project, meta });
        }
      } catch {
        if (active) {
          setAnswer(undefined);
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [project, fetchApi, discoveryApi, generation]);

  return {
    meta: answer && answer.project === project ? answer.meta : undefined,
    reload,
  };
}
